const toNumericId = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (/^-?\d+$/.test(trimmedValue)) {
      const normalizedValue = Number(trimmedValue);
      return Number.isFinite(normalizedValue) ? normalizedValue : null;
    }
  }

  return null;
};

const getMarkerCandidateId = (marker) => {
  if (marker && typeof marker === "object") {
    return marker.id ?? marker.marker_id ?? marker.markerId ?? null;
  }

  return marker;
};

export const getStatisticParameterId = (parameter) => {
  if (!parameter || typeof parameter !== "object") {
    return null;
  }

  return toNumericId(
    parameter.parameter_id ??
      parameter.id ??
      parameter.parameterId ??
      parameter.param_id ??
      null
  );
};

export const normalizeMarkerIds = (markers = []) => {
  if (!Array.isArray(markers) || markers.length === 0) {
    return [];
  }

  const seen = new Set();
  const normalized = [];

  markers.forEach((marker) => {
    const candidateId = getMarkerCandidateId(marker);
    if (candidateId === null || candidateId === undefined) {
      return;
    }

    const numericCandidate = toNumericId(candidateId);
    const normalizedCandidate =
      numericCandidate !== null ? numericCandidate : String(candidateId).trim();

    if (
      normalizedCandidate === null ||
      normalizedCandidate === undefined ||
      normalizedCandidate === ""
    ) {
      return;
    }

    const dedupeKey = String(normalizedCandidate);
    if (seen.has(dedupeKey)) {
      return;
    }

    seen.add(dedupeKey);
    normalized.push(normalizedCandidate);
  });

  return normalized;
};

export const collectFetchedMarkerIds = (results = []) => {
  if (!Array.isArray(results)) {
    return [];
  }

  return normalizeMarkerIds(
    results.map((item) => item?.id ?? item?.marker_id ?? item?.markerId ?? null)
  );
};

export const resolveOverviewClickMarkerIds = ({
  fetchedResults = [],
  localMarkers = [],
  useLocalFallback = false,
}) => {
  const fetchedMarkerIds = collectFetchedMarkerIds(fetchedResults);
  if (fetchedMarkerIds.length > 0) {
    return {
      markerIds: fetchedMarkerIds,
      source: "fetched",
    };
  }

  if (useLocalFallback) {
    const localMarkerIds = normalizeMarkerIds(localMarkers);
    if (localMarkerIds.length > 0) {
      return {
        markerIds: localMarkerIds,
        source: "local",
      };
    }
  }

  return {
    markerIds: [],
    source: "none",
  };
};

export const resolveOverviewDisplayCount = ({
  parameter,
  countOverridesByParameterId = {},
}) => {
  const parameterId = getStatisticParameterId(parameter);
  if (
    parameterId !== null &&
    Object.prototype.hasOwnProperty.call(countOverridesByParameterId, parameterId)
  ) {
    const overrideCount = Number(countOverridesByParameterId[parameterId]);
    if (Number.isFinite(overrideCount)) {
      return overrideCount;
    }
  }

  // Prefer the signature-deduped unique count returned by the API
  const uniqueCount = Number(
    parameter?.unique_markers_count ?? parameter?.unique_count ?? parameter?.event_count
  );
  if (Number.isFinite(uniqueCount) && uniqueCount > 0) {
    return uniqueCount;
  }

  const localMarkerCount = normalizeMarkerIds(parameter?.markers).length;
  if (localMarkerCount > 0) {
    return localMarkerCount;
  }

  const rawCount = parameter?.count;
  if (rawCount === null || rawCount === undefined) {
    return rawCount;
  }

  const normalizedCount = Number(rawCount);
  return Number.isFinite(normalizedCount) ? normalizedCount : rawCount;
};

const isPercentageParameter = (parameter) =>
  String(parameter?.parameter_name ?? parameter?.name ?? "").includes("%");

const toFiniteNumberOrNull = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const roundToHundredths = (value) => Math.round(value * 100) / 100;

const resolveAggregatedPercentage = ({
  valueSum,
  matchCount,
  numerator,
  denominator,
  hasComponentsInEveryMatch,
}) => {
  if (hasComponentsInEveryMatch) {
    return {
      count: denominator > 0 ? roundToHundredths((numerator * 100) / denominator) : 0,
      numerator,
      denominator,
    };
  }

  return {
    count: matchCount > 0 ? roundToHundredths(valueSum / matchCount) : 0,
  };
};

export const aggregatePlayerMatchStatistics = (matches = []) => {
  const normalizedMatches = Array.isArray(matches) ? matches : [];
  if (normalizedMatches.length === 0) {
    return [];
  }

  const totalsByParameterId = {};
  // Raw, un-deduped markers accumulated per parameter; normalized once at the end.
  // Deduping inside the loop (over the full accumulated array each iteration) is O(n^2)
  // in marker count and can freeze the main thread for players/teams with many matches.
  const rawMarkersByParameterId = {};
  // Percentages can't be added up across matches (25% + 50% is not 75%). Pool the
  // numerator and denominator the API returns with them instead, and fall back to the
  // per-match average for percentages that come without them.
  const percentagesByParameterId = {};

  normalizedMatches.forEach((match) => {
    const parameters = Array.isArray(match?.parameters)
      ? match.parameters
      : Array.isArray(match?.params)
        ? match.params
        : [];

    parameters.forEach((parameter) => {
      const parameterId = getStatisticParameterId(parameter);
      if (parameterId === null) {
        return;
      }

      const rawValue = parameter?.count ?? parameter?.value ?? parameter?.total ?? 0;
      const normalizedValue = Number(rawValue);
      if (!Number.isFinite(normalizedValue)) {
        return;
      }

      const safeUniqueValue = Number(
        parameter?.unique_markers_count ?? parameter?.unique_count ?? parameter?.event_count ?? 0
      );
      const normalizedUniqueValue = Number.isFinite(safeUniqueValue) ? safeUniqueValue : 0;

      if (!totalsByParameterId[parameterId]) {
        // A single match's numerator/denominator would mislabel the aggregate.
        const { numerator, denominator, ...parameterFields } = parameter;
        totalsByParameterId[parameterId] = {
          ...parameterFields,
          parameter_id: parameterId,
          count: 0,
          unique_count: 0,
          unique_markers_count: 0,
          markers: [],
        };
        rawMarkersByParameterId[parameterId] = [];

        if (isPercentageParameter(parameter)) {
          percentagesByParameterId[parameterId] = {
            valueSum: 0,
            matchCount: 0,
            numerator: 0,
            denominator: 0,
            hasComponentsInEveryMatch: true,
          };
        }
      }

      totalsByParameterId[parameterId].count += normalizedValue;
      totalsByParameterId[parameterId].unique_count += normalizedUniqueValue;
      totalsByParameterId[parameterId].unique_markers_count = totalsByParameterId[parameterId].unique_count;
      if (Array.isArray(parameter?.markers) && parameter.markers.length > 0) {
        // Just append here; dedup is done once after all matches are processed.
        rawMarkersByParameterId[parameterId].push(...parameter.markers);
      }

      const percentage = percentagesByParameterId[parameterId];
      if (percentage) {
        const numerator = toFiniteNumberOrNull(parameter?.numerator);
        const denominator = toFiniteNumberOrNull(parameter?.denominator);

        percentage.valueSum += normalizedValue;
        percentage.matchCount += 1;
        if (numerator === null || denominator === null) {
          percentage.hasComponentsInEveryMatch = false;
        } else {
          percentage.numerator += numerator;
          percentage.denominator += denominator;
        }
      }
    });
  });

  return Object.values(totalsByParameterId).map((parameter) => {
    const percentage = percentagesByParameterId[parameter.parameter_id];

    return {
      ...parameter,
      ...(percentage ? resolveAggregatedPercentage(percentage) : {}),
      markers: normalizeMarkerIds(rawMarkersByParameterId[parameter.parameter_id]),
    };
  });
};
